import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import PreviewIcon from "@mui/icons-material/Preview";
import IconButton from "@mui/material/IconButton";
import { getUserActivityCommits } from "../../../services/user.service";
import Loader from "../../../components/loader";
import TableCellWithToolTip from "../tableCellWithTooltip";
import { CombinedCommits, Commit } from "../../../common/interface"

type CommitsPopupProps = {
  open: boolean;
  user?: string;
  userId?: string;
  field: string;
  fromDate: string;
  toDate: string;
  platform: string;
  handleClose: () => void;
  issueKey: string[];
  selectedDateForPopup: string;
};

export const openInNewTab = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

const CommitsPopup: FC<CommitsPopupProps> = ({
  open,
  handleClose,
  user,
  field,
  fromDate,
  toDate,
  platform,
  issueKey,
  selectedDateForPopup,
}) => {
  const [isLoader, setIsLoader] = useState(false);
  const [gitLineChanges, setGitLineChanges] = useState<{
    insertions: number;
    deletions: number;
    total: number;
    addition_minus_deletions: number;
  }>({
    insertions: 0,
    deletions: 0,
    total: 0,
    addition_minus_deletions: 0,
  });
  const [commitsForSingleDate, setCommitsForSingleDate] = useState<Commit[]>(
    []
  );
  const [combinedCommits, setCombinedCommits] = useState<CombinedCommits>({});
  const [showAll, setShowAll] = useState(false);
  const handleCommits = async (
    fromDate: string,
    toDate: string,
    user: string,
    issueKey: string[]
  ) => {
    try {
      const response = await getUserActivityCommits(
        fromDate,
        toDate,
        user,
        issueKey
      );
      if (response.statusCode === 200 && response.data) {
        const allCommitsData = response.data.commits || {};
        setCombinedCommits(allCommitsData);
        const singleDateData = allCommitsData[`${selectedDateForPopup}`];

        const dateCommits = singleDateData?.commits || [];
        const totalAdditions = dateCommits.reduce(
          (sum: number, commit: Commit) =>
            sum + (commit.details?.additions || 0),
          0
        );
        const totalDeletions = dateCommits.reduce(
          (sum: number, commit: Commit) =>
            sum + (commit.details?.deletions || 0),
          0
        );
        const totalChanges = dateCommits.reduce(
          (sum: number, commit: Commit) =>
            sum + (commit.details?.total_changes || 0),
          0
        );

        setGitLineChanges({
          insertions: totalAdditions,
          deletions: totalDeletions,
          total: totalChanges,
          addition_minus_deletions: totalAdditions - totalDeletions,
        });

        setCommitsForSingleDate(dateCommits);
      }
      setIsLoader(false);
    } catch (err: any) {
      console.error(err);
      setIsLoader(false);
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => {
    if (open) {
      setIsLoader(true);
      if (field === "commits" && user) {
        handleCommits(fromDate, toDate, user, issueKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onCloseHandler = () => {
    if (isLoader) return;
    handleClose();
    setIsLoader(false);
    setTimeout(() => {
      setGitLineChanges({
        insertions: 0,
        deletions: 0,
        total: 0,
        addition_minus_deletions: 0,
      });
      setCommitsForSingleDate([]);
      setCombinedCommits({});
      setShowAll(false);
    }, 1000)
  };

  const renderAllCommits = useCallback(() => {
    return (
      <>
        {Object.keys(combinedCommits).length ? (
          <TableContainer component={Paper} sx={{
            maxHeight: '530px',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width={'14%'} sx={{ fontWeight: 400, background: "#003562", color: "#ffffff" }}>Date</TableCell>
                  <TableCell width={'60%'} sx={{ fontWeight: 400, background: "#003562", color: "#ffffff" }}>message</TableCell>
                  <TableCell width={'18%'} sx={{ fontWeight: 400, background: "#003562", color: "#ffffff" }}>line changes</TableCell>
                  <TableCell width={'10%'} sx={{ fontWeight: 400, background: "#003562", color: "#ffffff" }}>commit </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(combinedCommits).map((commitDate) => {
                  const commits = combinedCommits[commitDate]?.commits || [];
                  return (
                    <>
                      {commits.length ? (
                        <>
                          {commits.map((commit: Commit, index: number) => (
                            <TableRow key={commit.id}>
                              {index === 0 && (
                                <TableCell
                                  rowSpan={
                                    commits.length
                                  }
                                  align="center"
                                  sx={{ verticalAlign: "middle" }}
                                  width={"7%"}
                                >
                                  {commitDate}
                                </TableCell>
                              )}
                              {index > 0 && null}
                              <TableCellWithToolTip
                                title={commit.message || commit.title}
                                align="left"
                              />
                              <TableCell>
                                <Typography variant="body2" fontSize={"14px"}>
                                  {commit.details?.total_changes || 0} total,
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={"green"}
                                  fontSize={"14px"}
                                >
                                  {commit.details?.additions + " " || 0}
                                  &nbsp;insertions(+),
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={"red"}
                                  fontSize={"14px"}
                                >
                                  {commit.details?.deletions || 0} deletions(-)
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  aria-label="commit"
                                  onClick={() =>
                                    commit.web_url &&
                                    openInNewTab(commit?.web_url || "#")
                                  }
                                >
                                  <PreviewIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      ) : (
                        <TableRow sx={{ width: "100%" }}>
                          <TableCell colSpan={4} width={"100%"} align="center">
                            No Commits available for {commitDate}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="body1">No Commits Available</Typography>
          </Box>
        )}
      </>
    );
  }, [combinedCommits]);

  return (
      <Dialog
        fullWidth={true}
        maxWidth={"md"}
        open={open}
        onClose={onCloseHandler}
      >
        <DialogTitle>
          Commits for {issueKey} {showAll ? "" : `on ${selectedDateForPopup}`}
        </DialogTitle>
        <DialogContent>
          <Box
            display={"flex"}
            width={"100%"}
            flexDirection={"column"}
            gap={"10px"}
          >
            {isLoader ? <></> : <Box
              width={"100%"}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-end"}
            >
              <Switch
                checked={showAll}
                onChange={(event) => {
                  setIsLoader(true);
                  setShowAll(event.target.checked)
                  setTimeout(() => setIsLoader(false), 1000)
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
              <Typography variant="body1">
                Show all commits for this ticket
              </Typography>
            </Box>}
            <Box>
              {!isLoader ? (
                <>
                  {!showAll ? (
                    <>
                      {commitsForSingleDate.length ? (
                        <>
                          <Box
                            display={"flex"}
                            justifyContent={"space-around"}
                            alignItems={"center"}
                          >
                            <Typography>Git Line Changes</Typography>
                            <Box
                              display={"flex"}
                              alignItems={"center"}
                              gap={"5px"}
                            >
                              <Typography variant="body2" fontSize={"14px"}>
                                {gitLineChanges.total} total,
                              </Typography>
                              <Typography
                                variant="body2"
                                color={"green"}
                                fontSize={"14px"}
                              >
                                {gitLineChanges.insertions} insertions(+),
                              </Typography>
                              <Typography
                                variant="body2"
                                color={"red"}
                                fontSize={"14px"}
                              >
                                {gitLineChanges.deletions} deletions(-)
                              </Typography>
                            </Box>
                          </Box>
                          <List
                            sx={{
                              width: "100%",
                              bgcolor: "background.paper",
                              maxHeight: "300px",
                              overflowY: "auto",
                            }}
                          >
                            <>
                              {commitsForSingleDate.map(
                                (commit: Commit, index) => (
                                  <ListItem
                                    secondaryAction={
                                      <IconButton
                                        aria-label="commit"
                                        onClick={() =>
                                          commit.web_url &&
                                          openInNewTab(commit?.web_url || "#")
                                        }
                                      >
                                        <PreviewIcon />
                                      </IconButton>
                                    }
                                    key={index}
                                  >
                                    <ListItemText
                                      primary={
                                        commit?.title || commit?.message || "-"
                                      }
                                      sx={{ width: "100%" }}
                                    />
                                  </ListItem>
                                )
                              )}
                            </>
                          </List>
                        </>
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body1">
                            No Commits Available
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <>{renderAllCommits()}</>
                  )}
                </>
              ) : (
                <Box>
                  <Loader loader={isLoader} />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isLoader}
            variant="contained"
            onClick={onCloseHandler}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default CommitsPopup;
